import { FC } from 'react';
import styled from '@emotion/styled';
import { Typography, Icon } from '@taleemabad/dsm/components';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';

// Styled Components
const ContentContainer = styled.div`
  padding: var(--rk-spacing-xl);
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
  }
`;

const TaskHeader = styled.div`
  margin-bottom: var(--rk-spacing-xxxl);
  padding: var(--rk-spacing-xl);
  background-color: var(--md-sys-color-surface-container);
  border-radius: var(--rk-spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-md);

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
    margin-bottom: var(--rk-spacing-xxl);
  }
`;

const AudioSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);
  margin-bottom: var(--rk-spacing-xxxl);

  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-md);
    margin-bottom: var(--rk-spacing-xxl);
  }
`;

const AudioCard = styled.div`
  padding: var(--rk-spacing-xl);
  background-color: var(--md-sys-color-surface);
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--rk-spacing-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
    gap: var(--rk-spacing-md);
  }
`;

const AudioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--rk-spacing-md);

  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-sm);
  }
`;

const PlayingIndicator = styled.div<{ isPlaying: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-xs);
  color: var(--md-sys-color-primary);
  opacity: ${(props) => (props.isPlaying ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  font-size: 14px;
  font-weight: 500;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  height: 48px;
  border-radius: var(--rk-spacing-md);
  outline: none;

  &::-webkit-media-controls-panel {
    background-color: var(--md-sys-color-surface-container);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px var(--rk-spacing-xl);
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: var(--rk-spacing-lg);
  border: 1px dashed var(--md-sys-color-outline);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.xs)} {
    padding: 40px var(--rk-spacing-lg);
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: var(--md-sys-color-on-surface-variant);
`;

const NavigationFooter = styled.div`
  margin-top: var(--rk-spacing-xxxl);
  padding: var(--rk-spacing-lg);
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: var(--rk-spacing-lg);
  text-align: center;

  ${mq(BreakpointSizes.xs)} {
    margin-top: var(--rk-spacing-xxl);
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: var(--rk-spacing-xl);
  background-color: var(--md-sys-color-error-container);
  border-radius: var(--rk-spacing-lg);
  color: var(--md-sys-color-on-error-container);
  margin-bottom: var(--rk-spacing-xl);
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-md);
`;

// Interfaces
export interface AudioItem {
  url: string;
  fileName: string;
}

export interface TaskInfo {
  taskId: string;
  taskNumber: number;
  audios: string[];
}

interface FlnAnswersAudioTemplateProps {
  task: TaskInfo | null;
  audios: AudioItem[];
  error: string | null;
  currentlyPlaying: string | null;
  onAudioPlay: (audioUrl: string) => void;
  onAudioPause: () => void;
  registerAudioRef: (
    audioUrl: string,
    element: HTMLAudioElement | null,
  ) => void;
  emptyStateMessage?: string;
  footerMessage?: string;
}

export const FlnAnswersAudioTemplate: FC<FlnAnswersAudioTemplateProps> = ({
  task,
  audios,
  error,
  currentlyPlaying,
  onAudioPlay,
  onAudioPause,
  registerAudioRef,
  emptyStateMessage = 'No audio files available for this task.',
  footerMessage = 'Play the audio files above to listen to task instructions and examples',
}) => {
  if (error) {
    return (
      <ContentContainer>
        <ErrorContainer>
          <Typography variant="textTitleMedium">{error}</Typography>
        </ErrorContainer>
      </ContentContainer>
    );
  }

  if (!task) {
    return (
      <ContentContainer>
        <EmptyState>
          <EmptyIcon>
            <Icon name="audio_file" />
          </EmptyIcon>
          <Typography variant="textBodyLarge">Tasks not found.</Typography>
        </EmptyState>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <TaskHeader>
        <Typography variant="textTitleLarge">Task {task.taskNumber}</Typography>
        <Typography variant="textBodyMedium">
          Total Audio Files: {audios.length}
        </Typography>
      </TaskHeader>

      {audios.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Icon name="audio_file" />
          </EmptyIcon>
          <Typography variant="textBodyLarge">{emptyStateMessage}</Typography>
        </EmptyState>
      ) : (
        <AudioSection>
          {audios.map((audio, index) => {
            const isPlaying = currentlyPlaying === audio.url;

            return (
              <AudioCard key={audio.url}>
                <AudioHeader>
                  <Typography variant="textTitleMedium">
                    Audio {index + 1}: {audio.fileName}
                  </Typography>

                  <PlayingIndicator isPlaying={isPlaying}>
                    <Icon name="volume_up" />
                    <span>Playing</span>
                  </PlayingIndicator>
                </AudioHeader>

                <AudioPlayer
                  ref={(el) => registerAudioRef(audio.url, el)}
                  controls
                  preload="metadata"
                  onPlay={() => onAudioPlay(audio.url)}
                  onPause={onAudioPause}
                  onEnded={onAudioPause}
                >
                  <source src={audio.url} type="audio/mpeg" />
                  <source src={audio.url} type="audio/wav" />
                  <source src={audio.url} type="audio/ogg" />
                  <source src={audio.url} type="audio/mp4" />
                  Your browser does not support the audio element.
                </AudioPlayer>
              </AudioCard>
            );
          })}
        </AudioSection>
      )}

      <NavigationFooter>
        <Typography variant="textBodySmall">{footerMessage}</Typography>
      </NavigationFooter>
    </ContentContainer>
  );
};
